<template>
	<v-row>
		<v-col>

			<ui-box :status="boxStatus" new-forms>

				<form-title title="Pass Categories">
					Manage the categories that can be applied to your places. You may also sort
					their display order.

					<template v-slot:button>
						<v-btn
								class="mt-1 rounded-lg"
								color="success"
								depressed
								:disabled="isSaving"
								outlined
								small
								@click="addCategory"
						>
							<v-icon>
								mdi-plus
							</v-icon>
							New{{ $vuetify.breakpoint.mdAndUp ? ' Category' : '' }}
						</v-btn>
					</template>
				</form-title>

				<form-divider inner/>

				<v-row>
					<v-col>
						<template v-if="allCategories">
							<bw-search-select
									v-model="passCategories"
									confirm-remove
									:confirm-remove-message="confirmRemoveCategoryMessage"
									hide-details="auto"
									:items="allCategories"
									item-text="name"
									item-value="id"
									label="Pass Categories"
									multiple
							>
								<template v-slot:item-label="{ item }">
									<div>
									<span :class="{ 'font-weight-bold': item.recommended }">
										{{ item.name }}
									</span>
										<template v-if="item.recommended">
											<v-chip class="ms-1 white--text" color="green" small>Recommended
											</v-chip>
										</template>
									</div>
								</template>
							</bw-search-select>
						</template>
						<template v-else>
							No categories. Add categories to assign to your places.
						</template>
					</v-col>
				</v-row>

				<v-row>
					<v-col cols="12">

						<ui-box no-padding>

							<template v-if="passCategories.length === 0">

								<div
										class="align-center d-flex justify-center w-100"
										style="height: 80px;"
								>
									Oops, no pass categories yet!
								</div>

							</template>

							<template v-else>

								<v-container
										v-if="$vuetify.breakpoint.mdAndUp"
										style="line-height: 1rem;"
								>
									<v-row no-gutters
												 class="font-weight-bold grey--text mb-0 text-caption text--darken-2"
									>
										<v-col cols="auto" style="min-width: 30px;">
											<v-btn
													class="ml-n1 white"
													depressed
													small
													@click="sortAlphabetically"
											>
												<v-icon size="24">mdi-sort-alphabetical-ascending</v-icon>
											</v-btn>
										</v-col>
										<v-col class="flex-grow-1 my-n4">
											<div class="d-flex flex-column">
												<div class="mt-3" style="line-height: 1rem;">Category</div>
												<div class="text-caption" style="line-height: 1rem;">
													Assigned Places
												</div>
											</div>
										</v-col>
										<v-col
												class="d-flex"
												cols="auto"
												style="min-width: 64px;"
										>
											Actions
										</v-col>
									</v-row>
								</v-container>

								<v-divider
										v-if="$vuetify.breakpoint.mdAndUp"
										class="opacity-80"
								></v-divider>

								<v-container style="line-height: 1rem;">
									<draggable
											v-model="passCategories"
											class="my-n4 sortables"
											direction="vertical"
											filter="input"
											tag="div"
											@start="onDragStart"
											@end="onDragEnd"
									>
										<transition-group
												type="transition"
												:name="!drag ? 'flip-list' : null"
										>
											<v-row
													v-for="categoryId in passCategories"
													:key="`${categoryId}`"
													align="center"
													class="ma-0"
													:class="{ 'has-error': !categoryHasVenues(categoryId) }"
													no-gutters
											>
												<v-col cols="auto" style="min-width: 30px;">
													<v-icon>mdi-drag-vertical</v-icon>
												</v-col>

												<template v-if="categoryHasVenues(categoryId)">
													<v-col class="flex-grow-1">
														<div class="font-weight-medium text-body-2">
															{{ getCategoryById(categoryId).name }}
														</div>

														<!-- Small screens count only -->
														<template v-if="$vuetify.breakpoint.smAndDown">
															<div class="text-body-2 grey--text text--darken-1">
																<span
																		v-if="categoryHasVenues(categoryId) > 1"
																>
																	{{
																		`${categoryHasVenues(categoryId)} places`
																	}}
																</span>
																<span v-else>
																	{{
																		`${categoryHasVenues(categoryId)} place`
																	}}
																</span>
															</div>
														</template>

														<!-- Medium screens show chips + count -->
														<template v-else>
															<div class="d-flex mt-2 pe-4 w-100">
																<div
																		:ref="`placesChips${categoryId}`"
																		class="overflow-hidden w-100"
																		style="height: 1.5rem;"
																>
																	<template
																			v-for="(placeId, idx) in getDealVenueCategory(categoryId).venueIds">

																		<v-chip
																				:key="`chip-${categoryId}-${placeId}`"
																				class="chip--select-multi me-2"
																				close
																				small
																				@click:close="removePlace(categoryId, idx)"
																		>
																			{{ getPlaceById(placeId).name }}
																		</v-chip>

																	</template>
																</div>

																<div class="d-flex justify-end" style="min-width: 75px;">
																	<div
																			v-if="getOverflowPlacesCount(categoryId) > 0"
																			:class="[
																				'overflow-count',
																				'mx-2 px-2',
																				'rounded-pill',
																				'font-weight-bold secondary--text',
																				'text-caption text--darken-2 text-no-wrap',
																				'cursor-pointer'
																			]"
																			@click="editCategory(categoryId)"
																	>
																		{{ `+${getOverflowPlacesCount(categoryId)}` }}
																		{{ `${$vuetify.breakpoint.mdAndUp ? ' more' : ''}` }}
																	</div>
																</div>
															</div>
														</template>

													</v-col>
												</template>

												<template v-else>
													<v-col class="flex-grow-1">
														<div class="font-weight-medium text-body-2">{{
																getCategoryById(categoryId).name
															}}
														</div>
														<div class="align-center d-flex mt-1 text-caption">
															<span class="error--text mr-4 text--darken-2">
																Places are required.
															</span>
															<a
																	href="javascript:void(0);"
																	@click="editCategory(categoryId)"
															>Add Places</a>
															<v-divider class="mx-2" vertical></v-divider>
															<a
																	href="javascript:void(0);"
																	@click="addAllPlaces(categoryId)"
															>Add All</a>
														</div>
													</v-col>
												</template>

												<v-col
														class="d-flex"
														cols="auto"
														style="min-width: 50px;"
												>
													<v-btn
															class="me-2"
															color="secondary"
															depressed
															fab
															outlined
															small
															@click="editCategory(categoryId)"
													>
														<v-icon>
															mdi-pencil-outline
														</v-icon>
													</v-btn>
													<v-btn
															color="error"
															depressed
															fab
															outlined
															small
															@click="confirmRemoveCategory(categoryId)"
													>
														<v-icon>
															mdi-trash-can-outline
														</v-icon>
													</v-btn>
												</v-col>
											</v-row>
										</transition-group>
									</draggable>
								</v-container>

							</template>

						</ui-box>
					</v-col>
				</v-row>

			</ui-box>

			<save-form
					:refs="$refs"
					:additionalValidations="[!hasOrphanedCategories]"
					:is-saving="isSaving"
					new-forms
					save-text="Save"
					@cancel-action="exit"
					@save-action="save"
			></save-form>

			<v-dialog
					v-if="dialog.showAddCategory"
					content-class="rounded-xl"
					persistent
					scrollable
					:value="true"
					:width="600"
			>
				<v-form v-model="formIsValid.newCategory">
					<v-card class="position-relative" rounded="xl">
						<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
							<div class="w-100">
								<div class="align-center d-flex justify-space-between">
									<h6>New Category</h6>
								</div>
							</div>
						</v-card-title>
						<v-card-text class="text-body-2">
							<template v-if="newCategory">
								<v-row>
									<v-col>
										<v-text-field
												v-model="newCategory.name"
												class="rounded-lg required"
												hide-details="auto"
												label="Name"
												outlined
												type="text"
												:rules="rules.categoryName"
												@keydown.enter.prevent="saveNewCategory"
										></v-text-field>
									</v-col>
								</v-row>
							</template>
							<template v-else>Loading...</template>
						</v-card-text>
						<v-card-actions>
							<div class="align-center d-flex justify-space-between w-100">
								<div></div>
								<div class="d-flex justify-end">
									<v-btn
											class="me-2 me-md-4 rounded-lg"
											color="grey darken-1"
											depressed
											:loading="isSavingCategory"
											:large="$vuetify.breakpoint.mdAndUp"
											text
											@click="cancelNewCategory"
									>
										Cancel
									</v-btn>
									<v-btn
											class="rounded-lg"
											color="primary"
											depressed
											:disabled="!formIsValid.newCategory"
											:large="$vuetify.breakpoint.mdAndUp"
											:loading="isSavingCategory"
											@click="saveNewCategory"
									>
										Save
									</v-btn>
								</div>
							</div>
						</v-card-actions>
					</v-card>
				</v-form>
			</v-dialog>

			<v-dialog
					v-if="dialog.showEditCategory"
					content-class="rounded-xl"
					persistent
					scrollable
					:value="true"
					:width="600"
			>
				<v-form v-model="formIsValid.editCategory">
					<v-card class="position-relative" rounded="xl">
						<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
							<div class="w-100">
								<div class="align-center d-flex justify-space-between">
									<h6>Edit Pass Category</h6>
								</div>
							</div>
						</v-card-title>
						<v-card-text class="text-body-2">
							<template v-if="editingCategory">
								<v-row>
									<v-col>
										<v-text-field
												v-model="editingCategory.name"
												class="rounded-lg required"
												:disabled="editingCategory.partner_id === 0"
												hide-details="auto"
												label="Name"
												outlined
												type="text"
												:rules="rules.categoryName"
										></v-text-field>
									</v-col>
								</v-row>

								<v-row>
									<v-col>

										<bw-search-select
												v-model="editingDealVenueCategory.venueIds"
												:custom-search="customFilterFunction()"
												:items="places"
												item-text="name"
												item-value="id"
												label="Category Places"
												multiple
												sort-alpha
										>
											<template v-slot:item-label="{ item }">
												<div>{{item.name}}</div>
												<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
													<div v-for="(overwrittenName) in item.overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
														{{ overwrittenName }}
													</div>
												</div>
											</template>
										</bw-search-select>

									</v-col>
								</v-row>
							</template>
							<template v-else>Loading...</template>
						</v-card-text>
						<v-card-actions>
							<div class="align-center d-flex justify-space-between w-100">
								<div></div>
								<div class="d-flex justify-end">
									<v-btn
											class="me-2 me-md-4 rounded-lg"
											color="grey darken-1"
											depressed
											:large="$vuetify.breakpoint.mdAndUp"
											text
											@click="dialog.showEditCategory = false"
									>
										Cancel
									</v-btn>
									<v-btn
											class="rounded-lg"
											color="primary"
											depressed
											:large="$vuetify.breakpoint.mdAndUp"
											@click="updateCategory"
									>
										Update
									</v-btn>
								</div>
							</div>
						</v-card-actions>
					</v-card>
				</v-form>
			</v-dialog>

			<v-dialog
					v-if="dialog.showConfirmRemoveCategory"
					content-class="rounded-xl"
					persistent
					scrollable
					:value="true"
					:width="600"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Remove Category</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">
						This category will be removed from your pass and will no longer show for any
						of your places. Would you like to continue?
					</v-card-text>
					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<div></div>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="dialog.showConfirmRemoveCategory = false"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										@click="removeCategory"
								>
									Remove
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-dialog>

		</v-col>
	</v-row>
</template>

<script>
import {searchHelpers} from "../../../searchHelpers";

export default {
	name: 'PassCategories',
	props: {
		dealVenueCategories: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		venueCategories: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			allCategories: null,
			categoryPlaceHeaders: [
				{
					align: 'start',
					mandatory: true,
					text: 'Name',
					value: 'name'
				}
			],
			chipObservers: new Map(),
			dialog: {
				showConfirmRemoveCategory: false,
				showAddCategory: false,
				showEditCategory: false
			},
			drag: false,
			draggingIndex: null,
			editingCategory: null,
			editingDealVenueCategory: null,
			formIsValid: {
				editCategory: false,
				newCategory: false
			},
			isSaving: false,
			isSavingCategory: false,
			lastVisibleIndex: {},
			localDealVenueCategories: [...this.dealVenueCategories],
			newCategory: null,
			passCategories: [...this.dealVenueCategories.map(category => category.id)],
			rules: {
				categoryName: [
					v => !!v || 'Category name is required.',
					v => v && !this.allCategories.map(category => category.name.toLowerCase()).includes(v.toLowerCase()) || 'Category names must be unique.'
				]
			}
		};
	},
	created() {
		const flattenedPlaceCategories = this.places.flatMap(place => place.venueCategories);

		this.allCategories = [...this.venueCategories].map(venueCat => ({
			...venueCat,
			recommended: flattenedPlaceCategories.some(placeCat => placeCat.id === venueCat.id)
		}));

		this.$nextTick(() => {
			this.initializeObservers();
		});
	},
	computed: {
		boxStatus() {
			// localDealVenueCategories.length ? 'success' : 'error'
			if(!this.localDealVenueCategories.length)
				return null;

			return !this.hasOrphanedCategories ? 'success' : 'error';
		},

		confirmRemoveCategoryMessage() {
			return 'This category will be removed from your pass and will no longer show for any of your places. Would you like to continue?';
		},

		dragOptions() {
			return {
				group: "categories"
			};
		},

		hasOrphanedCategories() {
			return this.localDealVenueCategories.some(category => !category.venueIds?.length);
		}
	},
	methods: {
		addAllPlaces(categoryId) {
			this.localDealVenueCategories = this.localDealVenueCategories.map(category =>
					category.id !== categoryId ? category : {
						...category,
						venueIds: this.places.map(place => place.id)
					}
			);
		},

		addCategory() {
			this.newCategory = new Category(this.partner);
			this.dialog.showAddCategory = true;
		},

		cancelNewCategory() {
			this.newCategory = null;
			this.dialog.showAddCategory = false;
		},

		categoryHasVenues(categoryId) {
			return !!this.getDealVenueCategory(categoryId).venueIds.length;
		},

		confirmRemoveCategory(categoryId) {
			this.editingCategory = this.getCategoryById(categoryId);
			this.dialog.showConfirmRemoveCategory = true;
		},

		customFilterFunction() {
			return searchHelpers.searchByNameAndOverwritten();
		},

		disconnectObservers() {
			this.chipObservers.forEach((observers, categoryId) => {
				observers.forEach(observer => observer.disconnect());
			});
			this.chipObservers.clear();
		},

		editCategory(categoryId) {
			this.editingCategory = { ...this.getCategoryById(categoryId) };
			this.editingDealVenueCategory = { ...this.getDealVenueCategory(categoryId) };
			this.dialog.showEditCategory = true;
		},

		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		formatItemText(item) {
			return item.recommended ? `${item.name} (Recommended)` : item.name;
		},

		getCategoryById(categoryId) {
			return this.allCategories?.find(category => category.id === categoryId) || {};
		},

		getCountOfHiddenChips(categoryId) {
			// Calculate the count of hidden chips
			const totalChips = this.categoryHasVenues(categoryId);
			const lastVisibleIndex = this.lastVisibleChips[categoryId];
			return totalChips - lastVisibleIndex - 1;
		},

		getDealVenueCategory(categoryId) {
			return this.localDealVenueCategories.find(category => category.id === categoryId);
		},

		getOverflowPlacesCount(categoryId) {
			const places = this.getDealVenueCategory(categoryId).venueIds;
			if(this.lastVisibleIndex[categoryId] < 0 || places.length < 2) return 0;
			return places.length - this.lastVisibleIndex[categoryId] - 1;
		},

		getPlaceById(placeId) {
			const place = this.places.find(place => place.id === placeId);
			return place || { name: 'Error' };
		},

		initializeObservers() {
			this.localDealVenueCategories.forEach(category => {
				const chipContainers = this.$refs[`placesChips${category.id}`];

				if(!chipContainers?.length) return;
				const chipContainer = chipContainers[0];

				if(chipContainer) {
					const observers = [];
					const options = {
						root: chipContainer,
						threshold: [0, 1]
					};

					// Initialize observer for each chip
					chipContainer.querySelectorAll('.v-chip').forEach((chip, idx) => {
						const observer = new IntersectionObserver((entries, observer) => {
							this.onChipIntersect(entries, category.id, idx, observer);
						}, options);
						observer.observe(chip);
						observers.push(observer);
					});

					this.chipObservers.set(category.id, observers);
				}
			});
		},

		onChipIntersect(entries, categoryId, idx, observer) {
			// Ensure lastVisibleIndex for this category is initialized
			if(this.lastVisibleIndex[categoryId] === undefined) {
				this.$set(this.lastVisibleIndex, categoryId, -1);
			}

			const entry = entries[0]; // Assuming one entry per observation

			// Check if the chip is visible
			if(entry.intersectionRatio === 1) {
				// If this chip is further than any previously fully visible chip, update 
				// lastVisibleIndex
				if(idx > this.lastVisibleIndex[categoryId]) {
					this.$set(this.lastVisibleIndex, categoryId, idx);
				}
			}
			// If the chip is not visible
			else {
				// This chip is not visible, so the lastVisibleIndex should at minimum be one
				// less that it. 
				if(idx - 1 < this.lastVisibleIndex[categoryId]) {
					this.$set(this.lastVisibleIndex, categoryId, idx - 1);
				}
			}
		},

		onDragEnd() {
			return '';
		},

		onDragStart() {
			return '';
		},

		onMove({ relatedContext, draggedContext }) {
			return true;
		},

		removeCategory() {
			const idx = this.passCategories.findIndex(category => category === this.editingCategory.id);
			this.passCategories.splice(idx, 1);
			this.editingCategory = null;
			this.dialog.showConfirmRemoveCategory = false;
		},

		removePlace(categoryId, idx) {
			const categoryIdx = this.localDealVenueCategories.findIndex(category => category.id === categoryId);
			this.localDealVenueCategories[categoryIdx].venueIds.splice(idx, 1);
		},

		sortAlphabetically() {
			const { getDealVenueCategory } = this;

			this.passCategories.sort(
					(
							a,
							b
					) => getDealVenueCategory(a).name.localeCompare(getDealVenueCategory(b).name)
			);
		},

		save() {
			this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/update-deal-venue-categories/${this.pass.id || ''}`;

			axios.put(url, { categories: this.localDealVenueCategories })
					.then(({ data }) => {
						const url = this.pass.id
								? `/partner-portal/${this.partner.id}/passes/${this.pass.id}/faqs`
								: `/partner-portal/${this.partner.id}/passes/${this.pass.id}`
						window.location = url;
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		saveNewCategory() {
			// this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/create-venue-category/${this.pass.id || ''}`;

			axios.post(url, this.newCategory)
					.then(({ data }) => {
						this.allCategories.splice(this.allCategories.length, 0, data.category);
						this.passCategories.splice(this.passCategories.length, 0, data.category.id);
						this.newCategory = null;
						this.dialog.showAddCategory = false;
						// this.$nextTick(() => {
						// 	this.editCategory(data.category.id);
						// });
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		shouldShowCountChip(categoryId, index) {
			// Determine if this chip should display the count
			return this.lastVisibleChips[categoryId] === index;
		},

		syncCategories(passCategories) {
			const categoryMap = new Map(this.localDealVenueCategories.map(cat => [
				cat.id,
				cat
			]));

			const newLocalDealVenueCategories = passCategories.map(catId => {
				if(categoryMap.has(catId)) {
					const existingCategory = categoryMap.get(catId);
					return existingCategory;
				}
				else {
					const category = this.getCategoryById(catId);
					let venueIds = [];
					if(category.recommended) {
						venueIds = [...this.places]
								.filter(place => place.venueCategories
										.some(category => category.id === catId))
								.map(place => place.id);
					}
					const newCategory = {
						id: catId,
						name: category.name,
						venueIds
					};
					return newCategory;
				}
			});

			this.localDealVenueCategories = newLocalDealVenueCategories;
		},

		updateCategory() {
			const categoryId = this.editingCategory.id;

			// Save newName if different
			if(this.getCategoryById(categoryId).name !== this.editingCategory.name) {
				this.getDealVenueCategory(categoryId).newName = this.editingDealVenueCategory.name;
			}

			// Update category name in allCategories
			this.getCategoryById(categoryId).name = this.editingCategory.name;

			// Save places to category (still requires saving the page)
			this.getDealVenueCategory(categoryId).venueIds = this.editingDealVenueCategory.venueIds;

			// Finish by clearing both data
			this.editingCategory = null;
			this.editingDealVenueCategory = null;

			// Close modal
			this.dialog.showEditCategory = false;
		},

		updateObservers() {
			// Disconnect existing observers
			this.disconnectObservers();

			// Reinitialize observers
			this.initializeObservers();
		},
	},
	watch: {
		localDealVenueCategories: {
			deep: true,
			handler() {
				this.$nextTick(() => {
					this.updateObservers();
				});
			}
		},

		passCategories: {
			immediate: true,
			handler(newVal) {
				this.syncCategories(newVal);
			}
		}
	},
	beforeDestroy() {
		this.disconnectObservers();
	}
}

function Category(partner) {
	// this.id = null;
	this.name = null;
	// this.partner_id = partner.id;
	// this.media = [];
	// this.typeLabel = null;
};
</script>

<style lang="scss" scoped>
.sortables > span > div {
	margin: 0 -1rem !important;
	padding: 1rem;

	&:not(.has-error):hover {
		background-color: #f0f8f0 !important;
	}

	&.has-error {
		background-color: #f8f0f0 !important;
	}

	&:not(:first-child):not(.sortable-drag) {
		border-top: solid 1px rgba(0, 0, 0, 0.096)
	}
}

::v-deep .overflow-count {
	background: rgba(73, 178, 228, 0.08);
	border: solid 1px rgba(73, 178, 228, 0.12);
}
</style>
